import type { BasePanel } from '@Components/panel/panel.types';
import { getUrlForVoicePreset } from '@Libraries/text-to-speech.library';
import {getAssetUrl} from '@Utils/s3.util';

export interface AiVoicePanelProps extends BasePanel {
  onAddAudioItem(itemId: string): Promise<void>;
  onClose?(): void;
  showBackButton?: boolean;
  contentClassname?: string;
  isFullScreen?: boolean;
}

export enum PromptSubfilterType {
  STANDARD,
  PLAYABLE,
}

export interface BaseSubfilter {
  id: number;
  name: string;
  type: PromptSubfilterType;
}

export interface PlayableSubfilter extends BaseSubfilter {
  type: PromptSubfilterType.PLAYABLE;
  soundtrack: string;
  avatarUrl?: string;
  description: string;
}

export interface StandardSubfilter extends BaseSubfilter {
  type: PromptSubfilterType.STANDARD;
}

export type PromptSubfilter = PlayableSubfilter | StandardSubfilter;

export enum AiVoicePanelErrorState {
  STANDARD,
  DAILY_LIMIT_REACHED,
}

export const DEFAULT_VOICE_SUBFILTER = 1;
const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/ai-text-to-speech/';

export function getVoiceFilters(): PlayableSubfilter[] {
  return [
    {
      id: DEFAULT_VOICE_SUBFILTER,
      name: 'Jordan',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(DEFAULT_VOICE_SUBFILTER),
      description: window.i18next.t('pmwjs_casual'),
      avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}jordan.png`),
    },
    {
      id: 2,
      name: 'Taylor',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(2),
      description: window.i18next.t('pmwjs_friendly'),
      avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}taylor.png`),
    },
    {
      id: 3,
      name: 'Chris',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(3),
      description: window.i18next.t('pmwjs_engaging'),
      avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}chris.png`),
    },
    {
      id: 4,
      name: 'James',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(4),
      description: window.i18next.t('pmwjs_formal'),
      avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}james.png`),
    },
    {
      id: 5,
      name: 'Allison',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(5),
      description: window.i18next.t('pmwjs_conversational'),
      avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}allison.png`),
    },
    {
      id: 6,
      name: 'Kristen',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(6),
      description: window.i18next.t('pmwjs_warm'),
      avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}kristen.png`),
    },
    {
      id: 7,
      name: 'Andrew',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(7),
      description: window.i18next.t('pmwjs_professional'),
      avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}andrew.png`),
    },
    {
      id: 8,
      name: 'Jodie',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(8),
      description: window.i18next.t('pmwjs_upbeat'),
      avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}jodie.png`),
    },
    {
      id: 9,
      name: 'Eleanor',
      type: PromptSubfilterType.PLAYABLE,
      soundtrack: getUrlForVoicePreset(9),
      description: window.i18next.t('pmwjs_calm'),
      avatarUrl: getAssetUrl(`${THUMBNAIL_BASE_URL}eleanor.png`),
    },
  ];
}

export enum AiVoicePanelDataKey {
  GENERATION_STATE = 'generationState',
  IS_AI_WRITER_OPEN = 'isAiWriterOpen',
}

export enum AiVoicePanelGenerationState {
  IDLE = 'IDLE',
  GENERATING = 'GENERATING',
  READY = 'READY',
  ERROR = 'ERROR',
}

export const LOADING_KEY_PREFIX = 'ai-voice-loading';
